export type UtmParams = {
	utm_source?: string;
	utm_medium?: string;
	utm_campaign?: string;
	utm_term?: string;
	utm_content?: string;
};

export const UTM_PARAM_KEYS: (keyof UtmParams)[] = [
	"utm_source",
	"utm_medium",
	"utm_campaign",
	"utm_term",
	"utm_content",
];

export const CLICK_ID_PARAM_KEYS = [
	"gclid",
	"fbclid",
	"gbraid",
	"wbraid",
] as const;

export type ClickIdKey = (typeof CLICK_ID_PARAM_KEYS)[number];

export type ClickIdParams = Partial<Record<ClickIdKey, string>>;

export type AttributionQueryParams = Partial<UtmParams> & ClickIdParams;

export const GCLID_COOKIE = "_gcl_aw";
export const GBRAID_COOKIE = "_gcl_gb";
export const WBRAID_COOKIE = "_gcl_ag";
export const FBCLID_COOKIE = "_fbc";

export const CLICK_ID_COOKIE_NAMES: Record<ClickIdKey, string> = {
	gclid: GCLID_COOKIE,
	gbraid: GBRAID_COOKIE,
	wbraid: WBRAID_COOKIE,
	fbclid: FBCLID_COOKIE,
};

export const UTM_SESSION_COOKIE = "utm_session";
export const UTM_FIRST_TOUCH_COOKIE = "utm_first_touch";
export const CLICK_ID_SESSION_COOKIE = "click_id_session";

// Matches GA4's default session window.
export const UTM_SESSION_TTL_SECONDS = 30 * 60;
// Pending marketing confirmation on the right attribution window.
export const UTM_FIRST_TOUCH_TTL_SECONDS = 90 * 24 * 60 * 60;

/**
 * Collect-protocol campaign fields (`cs`/`cm`/`cn`/`cc`/`ck`). The sGTM GA4
 * client copies these onto the event model used for session channel grouping.
 */
export type Ga4Campaign = {
	source?: string;
	medium?: string;
	name?: string;
	content?: string;
	term?: string;
};

const UTM_TO_CAMPAIGN: [keyof UtmParams, keyof Ga4Campaign][] = [
	["utm_source", "source"],
	["utm_medium", "medium"],
	["utm_campaign", "name"],
	["utm_content", "content"],
	["utm_term", "term"],
];

export function ga4CampaignFromUtm(
	params: UtmParams | null | undefined
): Ga4Campaign | undefined {
	if (!params) {
		return undefined;
	}
	const campaign: Ga4Campaign = {};
	for (const [utmKey, campaignKey] of UTM_TO_CAMPAIGN) {
		const value = params[utmKey];
		if (value) {
			campaign[campaignKey] = value;
		}
	}
	return Object.keys(campaign).length > 0 ? campaign : undefined;
}

export function extractUtmParams(
	searchParams: URLSearchParams
): UtmParams | null {
	const params: UtmParams = {};
	let hasAny = false;
	for (const key of UTM_PARAM_KEYS) {
		const value = searchParams.get(key);
		if (value) {
			params[key] = value;
			hasAny = true;
		}
	}
	return hasAny ? params : null;
}

export function encodeUtmCookieValue(params: UtmParams): string {
	return JSON.stringify(params);
}

export function decodeUtmCookieValue(
	value: string | undefined | null
): UtmParams | null {
	if (!value) {
		return null;
	}
	try {
		const parsed: unknown = JSON.parse(value);
		if (parsed && typeof parsed === "object") {
			return parsed as UtmParams;
		}
		return null;
	} catch {
		return null;
	}
}

/**
 * Session (last-touch) cookie value: new UTM params in the request overwrite
 * it; otherwise the existing value is preserved so an in-session page view
 * with no UTM params doesn't blank out attribution captured earlier in the
 * same session. Returns null when there's nothing to write (no existing
 * cookie and no UTM params on this request).
 */
export function resolveSessionUtmCookieValue(
	searchParams: URLSearchParams,
	existingCookieValue: string | undefined
): string | null {
	const incoming = extractUtmParams(searchParams);
	if (incoming) {
		return encodeUtmCookieValue(incoming);
	}
	return existingCookieValue ?? null;
}

/**
 * Renames first-touch UTM params to a `first_touch_` prefix so they can sit
 * alongside the (last-touch) `utm_*` properties on the same event without
 * colliding — downstream analysis needs both: which channel is credited for
 * this session vs. which channel originally acquired the visitor.
 */
export function toFirstTouchProperties(
	params: UtmParams | null
): Record<string, string> {
	if (!params) {
		return {};
	}
	const result: Record<string, string> = {};
	for (const [key, value] of Object.entries(params)) {
		if (value !== undefined) {
			result[`first_touch_${key}`] = value;
		}
	}
	return result;
}

/**
 * First-touch cookie value: written once and never overwritten, so it keeps
 * crediting the channel that originally brought the visitor in regardless of
 * later campaign clicks in the same or a later session. Returns null once
 * the cookie already exists (nothing to write) or there's no UTM data yet.
 */
export function resolveFirstTouchUtmCookieValue(
	searchParams: URLSearchParams,
	existingCookieValue: string | undefined
): string | null {
	if (existingCookieValue) {
		return null;
	}
	const incoming = extractUtmParams(searchParams);
	return incoming ? encodeUtmCookieValue(incoming) : null;
}

export function extractClickIdParams(
	searchParams: URLSearchParams
): ClickIdParams {
	const params: ClickIdParams = {};
	for (const key of CLICK_ID_PARAM_KEYS) {
		const value = searchParams.get(key);
		if (value) {
			params[key] = value;
		}
	}
	return params;
}

/**
 * Click-id session (last-touch) cookie value: mirrors
 * `resolveSessionUtmCookieValue`'s precedence rules, but for ad click ids
 * rather than UTM params. Kept as a separate cookie (not merged into
 * `UTM_SESSION_COOKIE`) so client-side consumers of the UTM cookie — e.g.
 * `registerUtmSuperProperties`, which spreads it straight into Mixpanel
 * super properties — don't pick up click ids by accident.
 */
export function resolveClickIdSessionCookieValue(
	searchParams: URLSearchParams,
	existingCookieValue: string | undefined
): string | null {
	const incoming = extractClickIdParams(searchParams);
	if (Object.keys(incoming).length > 0) {
		return JSON.stringify(incoming);
	}
	return existingCookieValue ?? null;
}

export function decodeClickIdCookieValue(
	value: string | undefined | null
): ClickIdParams | null {
	if (!value) {
		return null;
	}
	try {
		const parsed: unknown = JSON.parse(value);
		if (parsed && typeof parsed === "object") {
			return parsed as ClickIdParams;
		}
		return null;
	} catch {
		return null;
	}
}

/**
 * Google Conversion Linker cookies (`_gcl_aw`, `_gcl_gb`, `_gcl_ag`) store
 * `GCL.<timestamp>.<id>`. Multiple records are concatenated with `.GCL.`.
 */
export function parseGclCookieValue(
	value: string | undefined | null
): string | undefined {
	if (!value) {
		return undefined;
	}
	const match = /^GCL\.\d+\.(.+)$/.exec(value);
	if (!match?.[1]) {
		return undefined;
	}
	const [id] = match[1].split(".GCL.");
	return id || undefined;
}

/**
 * Meta `_fbc` cookie stores `fb.<subdomainIndex>.<creationTime>.<fbclid>`.
 */
export function parseFbcCookieValue(
	value: string | undefined | null
): string | undefined {
	if (!value) {
		return undefined;
	}
	const match = /^fb\.\d+\.\d+\.(.+)$/.exec(value);
	return match?.[1] || undefined;
}

/**
 * Merge attribution params onto a URL without clobbering existing query
 * keys or the hash. Returns the original string when nothing is added or
 * the URL cannot be parsed.
 */
export function mergeAttributionQueryParams(
	url: string,
	params: AttributionQueryParams
): string {
	let parsed: URL;
	try {
		parsed = new URL(url);
	} catch {
		return url;
	}

	let changed = false;
	for (const [key, value] of Object.entries(params)) {
		if (!value || parsed.searchParams.has(key)) {
			continue;
		}
		parsed.searchParams.set(key, value);
		changed = true;
	}

	return changed ? parsed.toString() : url;
}
